/**
 * Mobile bottom bar: every item shows icon + name; the row scrolls horizontally
 * when items don't fit and keeps the active item in view. Active item is a
 * brand-colored pill behind the icon with a bold label.
 * raised: lifts the bar above an open sheet's backdrop so another icon works in one tap.
 * items: [{ key, label, active, badge?, icon(size) => node, onClick(event) }]
 */
import { useEffect, useRef } from "react";

export default function MobileBottomBar({ items, ariaLabel, raised = false }) {
  const activeRef = useRef(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
  }, [items.find((item) => item.active)?.key]);

  return (
    <nav
      aria-label={ariaLabel}
      className={`md:hidden fixed bottom-0 inset-x-0 ${raised ? "z-[60]" : "z-40"} flex items-stretch overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden bg-white dark:bg-neutral-bg2 shadow-[0_-2px_8px_-2px_rgba(0,0,0,0.08)] dark:border-t dark:border-white/10 pb-[env(safe-area-inset-bottom)]`}
    >
      {items.map((item) => {
        const badge = Number(item.badge) || 0;
        return (
        <button
          key={item.key}
          data-nav-key={item.key}
          type="button"
          aria-current={item.active ? "page" : undefined}
          ref={item.active ? activeRef : undefined}
          onClick={item.onClick}
          className={`flex-1 min-w-[72px] flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] transition-colors duration-150 active:scale-95 ${
            item.active
              ? "text-brand-blue dark:text-blue-300 font-bold"
              : "text-neutral-500 dark:text-slate-400 font-medium"
          }`}
        >
          <span
            className={`flex items-center justify-center h-8 w-14 rounded-full transition-all duration-200 ${
              item.active ? "bg-brand-blue/10 dark:bg-blue-300/15" : "bg-transparent"
            }`}
          >
            <span className="relative inline-flex">
              {item.icon(22)}
              {badge > 0 && (
                <span
                  className="absolute -top-1 -right-2 min-w-[14px] h-[14px] bg-red-500 text-white rounded-full text-[8px] font-bold flex items-center justify-center px-0.5 leading-none"
                  aria-label={`${badge} pending`}
                >
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </span>
          </span>
          <span className="truncate max-w-full px-1">{item.label}</span>
        </button>
        );
      })}
    </nav>
  );
}
